import clsx from 'clsx';
import { useCurrentNewsRibbon } from '@/context/NewsRibbon';
import { useNavigate } from '@/hooks/useNavigate';
import React, { useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { DataLayerImpressionPopUp } from '../DataLayerImpressionPopUp';
import { NewsRibbonProps } from '../NewsRibbon.interface';
import { useBannerResize } from '../useBannerResize';
import useStyles from './BannerRibbon.styles';

const dataLayerPosition = 'news_ribbon_banner';

const BannerRibbon = (props: Br.PropsWithContentButtons<NewsRibbonProps>) => {
  const {
    backgroundColor,
    className,
    contentButtons,
    theme,
    htmlBanner,
    scriptsBanner,
    stylesBanner,
    datalayerVariant,
  } = props;
  const classes = useStyles({});
  const contentRef = React.useRef<HTMLDivElement>(null);
  const { close, closed } = useCurrentNewsRibbon();
  const { navigate } = useNavigate();
  const { height } = useBannerResize({ enabled: true, ref: contentRef, selector: '.gcb-banner' });
  const location = useLocation();
  const title = props?.parentTitle || props?.titleValue || '';

  const handleClose = useCallback(() => {
    const positionNode = contentRef?.current?.closest(
      '[data-datalayer-position]',
    ) as HTMLDivElement;
    const position = positionNode?.dataset?.datalayerPosition || '';
    contentRef?.current?.dispatchEvent(
      new CustomEvent('gtm:bannerribbon:close', {
        bubbles: true,
        detail: { target: { position, variantName: datalayerVariant, title } },
      }),
    );

    close(250);
  }, [close, datalayerVariant, title]);

  useEffect(() => {
    if (!contentRef?.current) return () => {};
    if (!scriptsBanner) return () => {};
    if (!navigate) return () => {};

    // eslint-disable-next-line
    const executeScript: any = new Function(`return ${scriptsBanner}`)();
    const remove = executeScript({ target: contentRef.current, navigate, close: handleClose });

    return () => {
      remove();
    };
  }, [close, handleClose, navigate, scriptsBanner]);

  const rootClassName = clsx(
    classes.root,
    `${theme}-theme`,
    `bg-color-${backgroundColor}`,
    className,
    'news-ribbon',
  );

  return (
    <>
      <section
        data-testid="qa-news-ribbon-component"
        className={rootClassName}
        data-datalayer-position={dataLayerPosition}
        data-datalayer-variant={datalayerVariant}
        data-datalayer-title={title}
        style={{ height, marginTop: closed ? -height : 0 }}
      >
        {contentButtons?.content}
        {stylesBanner && (
          <Helmet>
            <style>{stylesBanner}</style>
          </Helmet>
        )}
        <div
          ref={contentRef}
          className={clsx(classes.content, 'gtm-news-ribbon-click')}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: htmlBanner || '' }}
        />
      </section>
      <DataLayerImpressionPopUp
        title={title}
        position={dataLayerPosition}
        {...(props?.isGlobalNewsRibbon && { key: location.pathname })}
      />
    </>
  );
};

export default BannerRibbon;
